import React, { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import c from 'classnames';
import { useHistory } from 'react-router-dom';

import './index.scss';

import Stepper from '../../components/Stepper';
import Location from '../Location';
import Report from '../Report';
import Information from '../Information';
import { formId } from '../../components/Form/ReportFormModel';
import { NOTIFICATION_TYPE_ERROR, THANKS_PAGE_SUFFIX, CONTACT_URL } from '../../config/constants';
import ValidationFormSchema from '../../components/Form/ValidationFormSchema';
import Alert from '../../components/Notification/Alert';
import DataContext from '../../contexts/DataContext';
import { fetchAddress } from '../../helpers/fetchAddress';
import { API_URL, API_URL_OPTIONS } from '../../../../shared/config/constants';

const Home = () => {
  const {
    latlng,
    updateInBounds,
    inBounds,
    validationSchema,
    updateValidationSchema,
    initialValues,
    categoryId,
    updateAddress,
    updateLatLng,
    updateCategories,
    alert,
    updateAlert,
  } = useContext(DataContext);

  const history = useHistory();
  const formikRef = useRef();

  const [active, _setActive] = useState(0);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const steps = ['location', 'report', 'information'];

  const classes = c(
    'button',
    'button--center',
    {
      'button--primary': active === 2,
      'button--secondary': active !== 2,
    });

  const activeComponent = () => {
    switch (active) {
      case 0:
        return <Location />;
      case 1:
        return <Report />;
      case 2:
        return <Information
          setIsAnonymous={setIsAnonymous}
          isAnonymous={isAnonymous}
        />;
      default:
        return <div>Not Found</div>;
    }
  };

  const goToStep = React.useCallback((step) => {
    _setActive(step);

    const mainContent = document.querySelector('#main-content');

    if (!mainContent) {
      return;
    }

    mainContent.setAttribute('tabindex', '-1');

    function removeTabIndexAttribute() {
      mainContent.removeAttribute('tabindex');
    }

    mainContent.addEventListener('blur', removeTabIndexAttribute);

    mainContent.focus();

    return () => {
      mainContent.removeEventListener('blur', removeTabIndexAttribute);
    };
  }, [_setActive]);

  useEffect(() => {
    document.activeElement.blur();

    history.push(history.location, active);

    // Use native back button when active > 0.
    const unlisten = history.listen((location, action) => {
      const isSkiplinkClick = location.hash === '#main-content' && !location.key;

      if (isSkiplinkClick) {
        return;
      }

      if (action === 'POP' && active !== 0) {
        goToStep(active - 1);
        updateValidationSchema(ValidationFormSchema(t)[active - 1]);
      } else {
        history.goBack();
      }
    });

    return () => {
      unlisten();
    };
  }, [active]);

  const textAlert = () => {
    if (inBounds === 'success') {
      return (t('reports.notification.location.success'));
    } else if (inBounds === 'error') {
      return (`${t('reports.notification.location.error')} <a href="${CONTACT_URL}" target="_blank">${t('reports.notification.location.error_contact')}</a>`);
    }
  };

  const toggleAlert = () => {
    if (inBounds !== '') {
      return (
        <Alert
          text={textAlert()}
          alert={inBounds}
        />
      );
    } else {
      return null;
    }
  };

  const canGoBack = () => {
    return (
      <button
        type="button"
        className={c('button button--center button--secondary')}
        onClick={() => {
          goToStep(active - 1);
          updateValidationSchema(ValidationFormSchema(t)[active - 1]);
        }}
      >
        {t('reports.button.back')}
      </button>
    );
  };

  const _handleSubmit = (values) => {
    if (active === (steps.length - 1)) {
      setSubmitting(true);

      const [lat, lng] = latlng;
      const basicData = {
        category_id: categoryId,
        description: values.description,
        location: {
          lat: Number(lat),
          lng: Number(lng),
          street: values.streetName,
          street_number: String(values.houseNumber),
          zipcode: values.postcode,
          place: values.location,
        },
        reporter: {
          anonymous: isAnonymous,
          ...(!isAnonymous && {
            name: values.fullName,
            ...(values.phoneNumber && values.phoneNumber.length > 0 && { phone: values.phoneNumber }),
            ...(values.emailAddress && values.emailAddress.length > 0 && { email: values.emailAddress }),
          }),
        },
      };

      fetch(`${API_URL}/v1/reports/submit`, {
        method: 'POST',
        headers: {
          ...API_URL_OPTIONS.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(basicData),
      })
        .then((res) => res.json())
        .then((data) => {
          const { id } = data.response;

          if (!values.images || values.images.length === 0) {
            window.location.replace(window.location + THANKS_PAGE_SUFFIX + '?id=' + id);

            return;
          }

          const imageData = new FormData();

          for (let i = 0; i < values.images.length; i++) {
            imageData.append(`images[${i}]`, values.images[i], values.images[i].name);
          }

          fetch(`${API_URL}/v1/reports/submit/${id}/images`, {
            method: 'POST',
            ...API_URL_OPTIONS,
            body: imageData,
          })
            .then((res) => {
              if (res.ok && res.status === 200) {
                window.location.replace(window.location + THANKS_PAGE_SUFFIX + '?id=' + id);
              } else {
                window.location.replace(window.location + THANKS_PAGE_SUFFIX + '?id=' + id + '&images_error=true');
              }
            })
            .catch((err) => {
              if (err) {
                window.location.replace(window.location + THANKS_PAGE_SUFFIX + '?id=' + id + '&images_error=true');
              }
            });
        })
        .catch((err) => {
          setSubmitting(false);
          if (err) {
            updateAlert({
              show: true,
              text: t('reports.notification.submission.error'),
              alert: NOTIFICATION_TYPE_ERROR,
            });
          }
        });
    } else {
      fetchAddress(
        updateAddress,
        updateLatLng,
        updateCategories,
        updateInBounds,
        formikRef,
        () => {
          updateInBounds('');
          goToStep(active + 1);
          updateAlert({ show: false });
          updateValidationSchema(ValidationFormSchema(t)[active + 1]);
        });
    }
  };

  return (
    <div>
      <Stepper
        active={active}
        steps={steps}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={((active === 2 && !isAnonymous) || active !== 2) && validationSchema}
        onSubmit={_handleSubmit}
        innerRef={formikRef}
      >
        <Form
          id={formId}
        >
          { activeComponent() }
          {active === 0 && toggleAlert()}
          {alert.show && active >= 1 && (
            <Alert
              text={alert.text}
              alert={alert.alert}
            />
          )}
          <div className={c('Group--Buttons')}>
            <button
              type="submit"
              className={classes}
              aria-disabled={!latlng || inBounds === 'error' || isSubmitting}
            >
              {active === 2 ? t('reports.button.submit') : t('reports.button.next')}
            </button>
            {active !== 0 ? canGoBack() : null}
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default Home;
